import styled from "styled-components";
import { TableHeaderItem } from "../../../hooks/useTable";
import { TableRow } from "../../molecules/table/TableRow";
import { useTable } from "./hook";
import { TableHeader } from "../../molecules/table/TableHeader";
import { useResponsive } from "../../../hooks/useResponsive";
import { LoadingIcon } from "../../atoms/icons/LoadingIcon";

type Props<DataType extends { selected?: boolean; disabled?: boolean }> = {
  headers: TableHeaderItem<DataType>[];
  data: DataType[];
  loading?: boolean;
  selectable?: boolean;
  editable?: boolean;
  deletable?: boolean;
  downloadable?: boolean;
  enableCSVDownload?: boolean;
  stickyHeader?: boolean;
  defaultEmptyText?: string;
  enableHoverPopup?: boolean;
  disableDeleteButton?: boolean;
  columnWidths?: string[]; // New prop for custom column widths
  showHeaderDeleteIcon?: boolean; // New prop to control header delete icon
  onEdit?: (data: DataType) => void;
  onDelete?: (data: DataType) => void;
  onSelect?: (data: DataType, checked: boolean) => void;
  onSelectAll?: (checked: boolean) => void;
};

export const Table = <
  DataType extends { disabled?: boolean; selected?: boolean },
>({
  headers,
  data,
  loading = false,
  selectable,
  editable,
  deletable,
  downloadable,
  stickyHeader,
  defaultEmptyText,
  disableDeleteButton,
  enableCSVDownload,
  enableHoverPopup,
  columnWidths,
  showHeaderDeleteIcon,
  onEdit,
  onDelete,
  onSelect,
  onSelectAll,
}: Props<DataType>) => {
  const { isSomeSelected } = useTable<DataType>(data);
  const { isMobile, isTablet } = useResponsive();

  return (
    <StyledTableWrapper $isMobile={isMobile}>
      <StyledTable $stickyHeader={stickyHeader} $editable={editable} $columnWidths={columnWidths} $isMobile={isMobile}>
        <TableHeader<DataType>
          headers={headers}
          selectable={!loading && data.length > 0 ? selectable : false}
          editable={!loading && data.length > 0 ? editable : false}
          deletable={!loading && data.length > 0 ? (showHeaderDeleteIcon !== undefined ? showHeaderDeleteIcon : deletable) : false}
          onSelectAll={onSelectAll}
          disableDeleteButton={disableDeleteButton}
          isChecked={data.some((item) => item.selected && !item.disabled)}
          isSomeSelected={isSomeSelected}
          columnWidths={columnWidths}
        />

        <tbody>
          {loading && (
            <tr className={`loading`}>
              <td colSpan={headers.length}>
                <StyledLoadingContainer $isMobile={isMobile}>
                  <LoadingIcon />
                  <StyledLoadingText $isMobile={isMobile}>Loading...</StyledLoadingText>
                </StyledLoadingContainer>
              </td>
            </tr>
          )}
          {!loading && data.length > 0 &&
            data.map((item, i) => (
              <TableRow
                key={`table-row-${(item as any).id ?? i}`}
                headers={headers}
                data={item}
                selectable={selectable}
                editable={editable}
                deletable={deletable}
                downloadable={downloadable}
                enableCSVDownload={enableCSVDownload}
                enableHoverPopup={enableHoverPopup}
                onEdit={onEdit}
                onSelect={onSelect}
                onDelete={onDelete}
                columnWidths={columnWidths}
              />
            ))}
          {!loading && data.length === 0 && (
            <tr className={`empty`}>
              <td colSpan={headers.length}>
                <StyledEmptyTableText $editable={editable} $isMobile={isMobile}>
                  {defaultEmptyText ?? "There is no data"}
                </StyledEmptyTableText>
              </td>
            </tr>
          )}
        </tbody>
      </StyledTable>
    </StyledTableWrapper>
  );
};

const StyledTableWrapper = styled.div<{ $isMobile?: boolean }>`
  width: 100%;
  overflow-x: auto;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    border-radius: 6px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  }
  
  ${({ $isMobile }) =>
    $isMobile
      ? `
      -webkit-overflow-scrolling: touch;
      `
      : ""}
`;

const StyledTable = styled.table<{
  $stickyHeader?: boolean;
  $editable?: boolean;
  $columnWidths?: string[];
  $isMobile?: boolean;
}>`
  table-layout: auto;
  width: 100%;
  min-width: 800px; 
  border-collapse: collapse;
  background-color: white;
  border-radius: 8px;
  overflow: hidden;

  @media (max-width: 768px) {
    min-width: 600px;
    border-radius: 6px;
  }

  @media (max-width: 480px) {
    min-width: 400px;
  }

  thead {
    height: 40px;
    background-color: #f8f9fa;
    ${({ $stickyHeader }) =>
    $stickyHeader
      ? "position: sticky; top: 0; z-index: 1; background-color: #f8f9fa;"
      : ""}
    
    tr {
      ${({ $stickyHeader }) => ($stickyHeader ? "border: none;" : "")}
    }

    th {
      color: #000000;
      font-weight: 700;
      font-size: 14px;
      text-align: left;
      padding: 8px 16px;
      border-bottom: 1px solid #e9ecef;
      white-space: nowrap;
      min-width: 120px;
      max-width: 300px;

      @media (max-width: 768px) {
        font-size: 13px;
        padding: 6px 12px;
        min-width: 100px;
      }

      @media (max-width: 480px) {
        font-size: 12px;
        padding: 4px 8px;
        min-width: 80px;
      }
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid #f2f2f2;
      transition: background-color 0.2s ease;
      height: 40px;
      
      &:hover {
        background-color: #f8f9fa;
      }
      
      &.empty,
      &.loading {
        border-bottom: none;
      }

      @media (max-width: 768px) {
        height: 36px;
      }

      @media (max-width: 480px) {
        height: 32px;
      }
    }

    tr:last-child {
      border-bottom: none;
    }
  }

  th,
  td {
    padding: ${({ $editable }) => ($editable ? "8px 16px" : "8px 16px")};
    vertical-align: middle;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 1.2;
    color: #212529;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    height: 35px;

    @media (max-width: 768px) {
      padding: 6px 12px;
      font-size: 13px;
      height: 32px;
    }

    @media (max-width: 480px) {
      padding: 4px 8px;
      font-size: 12px;
      height: 28px;
    }

    button {
      margin: 0 auto;
      height: 32px;

      @media (max-width: 768px) {
        height: 28px;
      }

      @media (max-width: 480px) {
        height: 24px;
      }
    }
  }
  
  ${({ $isMobile }) =>
    $isMobile
      ? `
      -webkit-overflow-scrolling: touch;
      `
      : ""}
`;

const StyledEmptyTableText = styled.div<{ $editable?: boolean; $isMobile?: boolean }>`
  color: #999999;
  text-align: center;
  font-size: 14px;
  font-weight: 400;
  line-height: 200%;
  padding-top: ${({ $editable }) => ($editable ? "18px" : "10px")};

  @media (max-width: 768px) {
    font-size: 13px;
    padding-top: ${({ $editable }) => ($editable ? "16px" : "8px")};
  }

  @media (max-width: 480px) {
    font-size: 12px;
    padding-top: ${({ $editable }) => ($editable ? "14px" : "6px")};
  }
`;

const StyledLoadingContainer = styled.div<{ $isMobile?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 16px;

  @media (max-width: 768px) {
    padding: 32px 16px;
    gap: 12px;
  }

  @media (max-width: 480px) {
    padding: 24px 12px;
    gap: 8px;
  }
`;

const StyledLoadingText = styled.div<{ $isMobile?: boolean }>`
  color: #999999;
  text-align: center;
  font-size: 14px;
  font-weight: 400;

  @media (max-width: 768px) {
    font-size: 13px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;
